// src/app/argent/resasAffichage.jsx — pastilles et boutons des réservations,
// prestations et ajustements.
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { DS } from '../../lib/ui'
import { uid } from '../../lib/format'
import { pushTombstonesForResa } from '../../lib/resa-tombstones'
import { computeMonthsTouched, assertProrataPosable } from '../../lib/resa-prorata-splits'
import { computeInvoiceMonthByNights, isRelocatable, addMonthsMk, canMoveTarget, canEditMonth } from '../../lib/resa-invoice-month'
import { motifVerrouAjustements, canEditAjustements, upsertAjustement } from '../../lib/ajustements'
import { collectMontantOverrides } from '../../lib/data-projection'
import { isDraftInvoice } from '../../lib/invoices'
import { findBlockingVoyageurInvoice, findBlockingCommission, findBlockingVoyageurInvoiceForMonth } from '../../lib/invoice-guard'

export function resasAffichage({
  data, invoicesRaw, month, setConfirm, setResaModal, setProrataModal, setExtraModal, setAjustModal,
  monthLabel, saveData, v2Lids, getLogById, monthKeyToLabel, ensureEntry, moveResaV2,
  resolveNaturalResaV2, setProrataSplit, clearProrataSplit, moveResa, canMoveDir,
}) {
  // Pastille « rattachée manuellement » : month_override actif ET différent du
  // mois naturel (majorité de nuits).
  const renderOverrideBadge = (r) => {
    if (!r.month_override) return null
    const naturalMk = computeInvoiceMonthByNights(r.checkIn || r.date, r.checkOut)
    if (r.month_override === naturalMk) return null
    return (
      <span
        title={`Rattachée manuellement à ${r.month_override}`}
        style={{ marginLeft: 4, color: DS.terracotta || "#C17B5C", fontWeight: 700, cursor: "help" }}
      >↪</span>
    )
  }
  // Pastille « montant corrigé manuellement » (L1 V2-edit-montant) — distincte
  // de renderOverrideBadge (réservé au month_override). ✎ si montant_source==='override'.
  const renderMontantBadge = (r) => {
    if (r.montant_source !== 'override') return null
    return (
      <span
        title="Montant corrigé manuellement"
        style={{ marginLeft: 4, color: DS.terracotta || "#C17B5C", fontWeight: 700, cursor: "help" }}
      >✎</span>
    )
  }
  // Pastille « ménage corrigé manuellement » — ✎ si menage_source==='override'
  // (projection applyMenageOverrides). MIROIR de renderMontantBadge.
  const renderMenageBadge = (r) => {
    if (r.menage_source !== 'override') return null
    return (
      <span
        title="Ménage corrigé manuellement"
        style={{ marginLeft: 4, color: DS.terracotta || "#C17B5C", fontWeight: 700, cursor: "help" }}
      >✎</span>
    )
  }
  // Boutons ◀▶ de relocation mensuelle. Grisés si non ré-ancrable ou hors
  // amplitude/plancher dans la direction concernée.
  const renderMoveButtons = (lid, r) => {
    const okPrev = canMoveDir(lid, r, -1)
    const okNext = canMoveDir(lid, r, +1)
    const relocatable = isRelocatable(r)
    const btn = (dir, ok, label) => (
      <button
        onClick={ok ? () => moveResa(lid, r.id, dir) : undefined}
        disabled={!ok}
        title={
          !relocatable
            ? "Résa non ré-ancrable (pas de clé OTA/iCal)"
            : ok
              ? (dir < 0 ? "Rattacher au mois précédent" : "Rattacher au mois suivant")
              : "Déplacement hors limites (±1 mois / plancher mai 2026)"
        }
        style={{
          background: "none", border: "none", fontSize: 12, marginRight: 2,
          cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.3,
          color: "#666", padding: "0 1px",
        }}
      >{label}</button>
    )
    return (<>{btn(-1, okPrev, "◀")}{btn(+1, okNext, "▶")}</>)
  }
  // ── Lot 3 V2-move-resa — UI ◀▶ V2-aware (F1/NP-3) ──
  // canMoveDirV2 : amplitude ±1 (depuis le NATUREL computeInvoiceMonthByNights,
  // PAS findResaBucket — V2 n'a pas de bucket physique KV) + plancher. effectiveMk
  // via data._monthOverrides. false si pas de code stable (déplacement impossible).
  const canMoveDirV2 = (lid, r, dir) => {
    const code = (r && (r.airbnbCode || r.bookingRef)) || null
    if (!code) return false
    // AMENDEMENT M2 — XOR réciproque : une résa RÉPARTIE au prorata (⚖️) ne peut
    // pas être DÉPLACÉE (◀▶) — sémantiques incompatibles (sinon un MOVE écrase
    // silencieusement le prorata). Miroir du XOR côté POSE (assertProrataPosable).
    if (data._prorataSplits?.[lid]?.[code]) return false
    const naturalMk = computeInvoiceMonthByNights(r.checkIn || r.date, r.checkOut)
    if (!naturalMk) return false
    const effectiveMk = (data._monthOverrides?.[lid]?.[code]) || naturalMk
    const targetMk = addMonthsMk(effectiveMk, dir)
    if (!targetMk) return false
    const allowed = new Set([addMonthsMk(naturalMk, -1), naturalMk, addMonthsMk(naturalMk, 1)])
    if (!allowed.has(targetMk)) return false
    if (!canMoveTarget(targetMk)) return false
    if (!canEditMonth(naturalMk) || !canEditMonth(targetMk)) return false
    return true
  }
  // Boutons ◀▶ V2 — miroir renderMoveButtons mais via canMoveDirV2 + moveResaV2.
  // ◀▶ désactivés + tooltip dédié si pas de code stable.
  const renderMoveButtonsV2 = (lid, r) => {
    const hasCode = !!(r && (r.airbnbCode || r.bookingRef))
    const code = (r && (r.airbnbCode || r.bookingRef)) || null
    // AMENDEMENT M2 — tooltip dédié si la résa est répartie au prorata (◀▶ grisés
    // par canMoveDirV2, on EXPLIQUE pourquoi au survol).
    const isProrated = !!(code && data._prorataSplits?.[lid]?.[code])
    const okPrev = canMoveDirV2(lid, r, -1)
    const okNext = canMoveDirV2(lid, r, +1)
    const btn = (dir, ok, label) => (
      <button
        onClick={ok ? () => moveResaV2(lid, r, dir) : undefined}
        disabled={!ok}
        title={
          !hasCode
            ? "Résa sans code stable (Airbnb/Booking)"
            : isProrated
              ? "Réservation répartie au prorata (⚖️) : retirez la répartition avant de déplacer."
              : ok
                ? (dir < 0 ? "Rattacher au mois précédent" : "Rattacher au mois suivant")
                : "Déplacement hors limites (±1 mois / plancher mai 2026)"
        }
        style={{
          background: "none", border: "none", fontSize: 12, marginRight: 2,
          cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.3,
          color: "#666", padding: "0 1px",
        }}
      >{label}</button>
    )
    return (<>{btn(-1, okPrev, "◀")}{btn(+1, okNext, "▶")}</>)
  }
  // Pastille V2 « rattachée manuellement » — lit l'estampille month_moved posée
  // par la projection (applyMonthMoves). DISTINCTE de renderOverrideBadge qui lit
  // r.month_override (V1, jamais posé en V2).
  const renderMoveBadgeV2 = (r) => {
    if (!r || !r.month_moved) return null
    return (
      <span
        title={`Rattachée manuellement à ${r.month_moved}`}
        style={{ marginLeft: 4, color: DS.terra || "#C17B5C", fontWeight: 700, cursor: "help" }}
      >↪</span>
    )
  }

  // ── Lot 5c V2-prorata — OUVERTURE de la modale (calcul des props, réplique B1) ──
  // Réplique EXACTE de la dérivation de setProrataSplit : on part de la résa
  // NATURELLE (resolveNaturalResaV2), on applique l'override montant éventuel
  // (collectMontantOverrides) pour obtenir montantTotal POST-OVERRIDE / PRÉ-
  // PRORATA (B1), on calcule monthsTouched/frozenSet DATE-BOUNDED (mêmes mois du
  // séjour que le moteur, indépendants de la fenêtre) puis on pré-bind posableFn sur
  // assertProrataPosable. La modale ne re-dérive RIEN.
  const openProrataModal = (lid, r) => {
    const code = (r && (r.airbnbCode || r.bookingRef)) || null
    if (!code) { alert('Réservation non proratisable (pas de code stable).'); return }
    const log = getLogById(lid)
    if (!log) { alert('Logement introuvable — proratisation indisponible.'); return }
    const nat = resolveNaturalResaV2(lid, code)
    if (!nat || !nat.naturalResa || !Number.isFinite(nat.naturalMontant)) {
      alert('Réservation naturelle introuvable — proratisation indisponible.')
      return
    }
    // montantTotal POST-OVERRIDE (B1) : override montant s'il existe, sinon naturel.
    const overrides = collectMontantOverrides(data, v2Lids)
    const ov = overrides[lid] && overrides[lid][code]
    const montantTotal = Number.isFinite(Number(ov)) ? Math.round(Number(ov) * 100) / 100 : nat.naturalMontant
    // Mois touchés : DATE-BOUNDED (tout le séjour, indépendant de la fenêtre) —
    // MÊME source que le moteur (data-projection.js). Fenêtre plus un intrant.
    const monthsTouched = computeMonthsTouched(nat.naturalResa)
    const monthLabels = monthsTouched.map(monthKeyToLabel)
    // frozenSet : miroir EXACT de la garde gel de clearProrataSplit (3 findBlocking*).
    // Si la résa (série V) OU n'importe quel mois est gelé → on marque TOUS les
    // mois (le moteur fail-close globalement : un mois gelé ⇒ résa non-proratisable).
    const frozenSet = new Set()
    if (Array.isArray(invoicesRaw)) {
      let anyFrozen = false
      if (nat.naturalResa.id != null && findBlockingVoyageurInvoice(invoicesRaw, { reservationId: nat.naturalResa.id })) {
        anyFrozen = true
      }
      for (const mk of monthsTouched) {
        const label = monthKeyToLabel(mk)
        if (!label) continue
        if (
          findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel: label }) ||
          findBlockingVoyageurInvoiceForMonth(invoicesRaw, { logId: log.id, monthLabel: label })
        ) {
          anyFrozen = true
          break
        }
      }
      if (anyFrozen) for (const mk of monthsTouched) frozenSet.add(mk)
    }
    const existingSpec = data._prorataSplits?.[lid]?.[code] || null // M3 : objet BRUT
    const posableFn = (spec) =>
      assertProrataPosable({
        resa: nat.naturalResa,
        code,
        monthsTouched,
        montantTotal,
        spec,
        invoicesRaw,
        logId: log.id,
        monthKeyToLabel,
        existingMonthOverride: data._monthOverrides?.[lid]?.[code],
      })
    // MINEUR-1 — exclusivité mécanique : fermer resaModal avant d'ouvrir.
    setResaModal(null)
    setProrataModal({
      lid,
      code,
      logNom: log.nom,
      voyageur: nat.naturalResa.voyageur || r.voyageur || '',
      naturalResa: nat.naturalResa,
      naturalMontant: nat.naturalMontant,
      montantTotal,
      monthsTouched,
      monthLabels,
      frozenSet,
      existingSpec,
      posableFn,
      onSet: (spec) => { setProrataModal(null); return setProrataSplit(lid, r, spec) },
      onClear: () => { setProrataModal(null); return clearProrataSplit(lid, r) },
    })
  }

  // Bouton ⚖️ « Répartir au prorata » (miroir renderMoveButtonsV2). Affiché si
  // V2 + code stable + non manuelle. GRISÉ + tooltip si la résa est DÉPLACÉE
  // (◀▶) : il faut retirer le déplacement avant de proratiser (XOR réciproque).
  const renderProrataButton = (lid, r) => {
    const code = (r && (r.airbnbCode || r.bookingRef)) || null
    if (!code) return null
    if (r.source === 'manual') return null
    const isMoved = !!(data._monthOverrides?.[lid]?.[code])
    return (
      <button
        onClick={isMoved ? undefined : () => openProrataModal(lid, r)}
        disabled={isMoved}
        title={
          isMoved
            ? 'Réservation déplacée (◀▶) : retirez le déplacement avant de proratiser.'
            : 'Répartir au prorata sur plusieurs mois'
        }
        style={{
          background: 'none', border: 'none', fontSize: 13, marginRight: 4,
          cursor: isMoved ? 'not-allowed' : 'pointer', opacity: isMoved ? 0.3 : 1,
          color: DS.terra || '#C17B5C', padding: 0,
        }}
      >⚖️</button>
    )
  }

  // Pastille ⚖️ « réparti au prorata » (miroir renderMoveBadgeV2) : lit le
  // marqueur r._prorataOf posé par la projection sur les lignes proratisées.
  const renderProrataBadge = (r) => {
    if (!r || !r._prorataOf) return null
    return (
      <span
        title="Réparti au prorata"
        style={{ marginLeft: 4, color: DS.terra || '#C17B5C', fontWeight: 700, cursor: 'help' }}
      >⚖️</span>
    )
  }
  const deleteResa = (lid,resaId) => {
    const nd=JSON.parse(JSON.stringify(data))
    const resaToDelete = nd[month]?.[lid]?.reservations?.find(r => r.id === resaId)
    pushTombstonesForResa(nd, lid, resaToDelete)
    if (nd[month]?.[lid]?.reservations) nd[month][lid].reservations=nd[month][lid].reservations.filter(r=>r.id!==resaId)
    saveData(nd); setConfirm(null)
  }
  const saveExtra = (lid,extra) => {
    const nd=JSON.parse(JSON.stringify(data)); ensureEntry(nd,lid)
    const idx=nd[month][lid].extras.findIndex(e=>e.id===extra.id)
    if (idx>=0) nd[month][lid].extras[idx]=extra
    else nd[month][lid].extras.push({...extra,id:uid()})
    saveData(nd); setExtraModal(null)
  }
  const deleteExtra = (lid,extraId) => {
    const nd=JSON.parse(JSON.stringify(data))
    if (nd[month]?.[lid]?.extras) nd[month][lid].extras=nd[month][lid].extras.filter(e=>e.id!==extraId)
    saveData(nd); setConfirm(null)
  }
  // ── Ajustements (avoir, remise, parrainage) — ⚠️ CHEMIN ARGENT ──
  // factEmise (prédicat existant App.jsx) : facture déjà émise pour ce
  // logement/mois → modif verrouillée (désync facture). Garde combinée :
  // canEditMonth(plancher) ET !factEmise.
  // Source de vérité = table invoices (lignes brutes), PAS le factLog KV (#408).
  // Retourne la ligne brute bloquante (ou null) — canEditAjustements teste la
  // truthiness, compatible. monthLabel = mois affiché (contexte global).
  const ajustFactEmise = log =>
    findBlockingCommission(invoicesRaw, { logId: log.id, monthLabel })
  // Edit-by-id calqué sur saveExtra. `aj` = {id?, label, montant}. Le montant
  // négatif (déduction) est légitime ; isNaN est rejeté EN AMONT par la modale
  // (bouton désactivé). Garde défensive ici : on refuse aussi un montant non
  // numérique pour ne jamais persister un NaN/0 silencieux.
  // Le message dit POURQUOI (brouillon à supprimer ≠ mois facturé) :
  // motifVerrouAjustements, src/lib/ajustements.js.
  const motifVerrouAjust = log => {
    const bloquante = ajustFactEmise(log)
    return motifVerrouAjustements(month, bloquante, { estBrouillon: !!bloquante && isDraftInvoice(bloquante) })
  }
  const saveAjust = (log,aj) => {
    if (!canEditAjustements(month, ajustFactEmise(log))) {
      alert(motifVerrouAjust(log))
      setAjustModal(null); return
    }
    if (!aj || isNaN(parseFloat(aj.montant))) { setAjustModal(null); return }
    const lid = log.id
    const nd = JSON.parse(JSON.stringify(data))
    ensureEntry(nd,lid)
    // ensureEntry ne crée PAS `ajustements` → init défensive avant upsert.
    if (!nd[month][lid].ajustements) nd[month][lid].ajustements=[]
    nd[month][lid].ajustements = upsertAjustement(nd[month][lid].ajustements, aj, uid)
    saveData(nd); setAjustModal(null)
  }
  const deleteAjust = (log,ajId) => {
    if (!canEditAjustements(month, ajustFactEmise(log))) {
      alert(motifVerrouAjust(log))
      setConfirm(null); return
    }
    const nd=JSON.parse(JSON.stringify(data))
    if (nd[month]?.[log.id]?.ajustements) nd[month][log.id].ajustements=nd[month][log.id].ajustements.filter(a=>a.id!==ajId)
    saveData(nd); setConfirm(null)
  }

  const exportCSV = () => { alert("Export CSV disponible en production.") }
  return {
    renderOverrideBadge, renderMontantBadge, renderMenageBadge, renderMoveButtons,
    renderMoveButtonsV2, renderMoveBadgeV2, renderProrataButton, renderProrataBadge, deleteResa,
    saveExtra, deleteExtra, ajustFactEmise, motifVerrouAjust, saveAjust, deleteAjust,
  }
}
